import {AdyenCheckoutError, PaymentFailedData, UIElement} from '@adyen/adyen-web'
import * as Sentry from '@sentry/react'
import {ErrorMessage} from 'components/common/ErrorMessage'
import {FixedFooter} from 'components/common/FixedFooter'
import {Button} from 'components/system/Button'
import {usePreviousStepperPath} from 'hooks/usePreviousStepperPath'
import {useConfirmingPath, useCurrency, usePaymentPath, useSiteUrl} from 'providers/EnvironmentProvider'
import {useCrossSelling, useOrderDetailsMutators} from 'providers/OrderDetailsProvider'
import {OrderProvider} from 'providers/OrderProvider'
import {useCallback, useState} from 'react'
import {AdyenCardPayment} from 'types/ventrata'
import addTrackingBreadcrumb, {BreadcrumbLevel, BreadcrumbType} from 'utils/addTrackingBreadcrumb'
import {ga4} from 'utils/ga4'
import {getGa4OrderItems} from 'utils/getGa4OrderItems'
import {AdyenDropin} from './AdyenDropin'

declare global {
  interface Window {
    ApplePaySession?: any
  }
}

type Props = {
  onReady: () => void
}

export const AdyenForm = ({onReady}: Props) => {
  const siteUrl = useSiteUrl()
  const currency = useCurrency()
  const crossSelling = useCrossSelling()
  const confirmingPath = useConfirmingPath()
  const paymentPath = usePaymentPath()
  const previousPath = usePreviousStepperPath(paymentPath)
  const order = OrderProvider.useOrder()
  const {setPaymentType} = useOrderDetailsMutators()
  const [error, setError] = useState<string | null>(null)

  const backToPrevious = useCallback(() => {
    window.location.href = previousPath
  }, [previousPath])

  const backToPay = useCallback(() => {
    window.location.reload()
  }, [])

  const back = error ? backToPay : backToPrevious

  const onPaymentTypeChange = (paymentType: string) => {
    if (!paymentType || paymentType === 'link') {
      return
    }

    ga4({
      event: 'add_payment_info',
      ecommerce: {
        payment_type: paymentType,
        checkout_type: 'custom_checkout',
        items: getGa4OrderItems(order, currency, crossSelling),
      },
    })

    addTrackingBreadcrumb({
      type: BreadcrumbType.Default,
      level: BreadcrumbLevel.Info,
      message: 'Payment type changed: ' + paymentType,
      data: {
        payment_type: paymentType,
      },
    })

    setPaymentType(paymentType)
  }

  const onPaymentCompleted = (result: any, component: any) => {
    Sentry.captureMessage('Adyen payment COMPLETED', 'info')

    addTrackingBreadcrumb({
      type: BreadcrumbType.Default,
      level: BreadcrumbLevel.Info,
      message: 'Adyen payment completed',
    })

    window.location.href = `${siteUrl}${confirmingPath}`
  }

  const onPaymentFailed = (data: PaymentFailedData, element: UIElement) => {
    Sentry.captureMessage('Adyen payment FAILED', 'error')

    addTrackingBreadcrumb({
      type: BreadcrumbType.Default,
      level: BreadcrumbLevel.Error,
      message: 'Adyen payment FAILED',
      data: data,
    })

    setError('Sorry, your payment was not successful. Please press back and try again.')
  }

  const onError = (error: AdyenCheckoutError) => {
    Sentry.captureException(error)
  }

  const cardPayment = order.cardPayment as AdyenCardPayment

  return (
    <form className="flex flex-col gap-6" id="payment">
      {!!order.recaptchaEnterpriseReceived && (
        <AdyenDropin
          key={cardPayment.adyen.session?.id}
          clientKey={cardPayment.adyen.clientKey}
          session={cardPayment.adyen.session}
          environment={cardPayment.adyen.environment}
          amount={cardPayment.balance}
          currency={cardPayment.currency}
          countryCode={cardPayment.adyen.countryCode}
          locale="en-US"
          showPayButton={true}
          paymentMethodsConfiguration={cardPayment.adyen.paymentMethodsConfiguration}
          onPaymentCompleted={onPaymentCompleted}
          onPaymentFailed={onPaymentFailed}
          onError={onError}
          onPaymentTypeChange={onPaymentTypeChange}
          onReady={onReady}
        />
      )}

      {error && <ErrorMessage error={error} />}

      <FixedFooter>
        <div className="flex gap-2">
          <Button.Back onClick={back} className="block d:hidden" />
        </div>
      </FixedFooter>
    </form>
  )
}
